import { useEffect, useState } from 'react';
import { useConciergeContext } from '../ConciergeContextProvider';
import { useBagWords } from '../../utils/hooks/useBagWords';

/**
 * The concierge's bag control.
 *
 * The concierge is full-viewport (sidebar + chat) — the landing-page nav and
 * its bag badge never render here — so this button is the concierge's ONLY bag
 * UI. It appears whenever the host injected a commerce adapter, i.e. whenever
 * this app has a bag at all; which bag (Brandclub or Shopify) is the adapter's
 * business, not this component's.
 *
 * Shape: the storefront bag, deliberately, with the count drawn INSIDE it — the
 * same language `CustomDtcBagButton` speaks on the brand's own storefront, so a
 * shopper who has seen one recognises the other. The count is the whole signal;
 * a pulsing dot rode along for a while and was just noise beside it.
 *
 * It replaced a "Bag · n | View bag | Checkout" pill: three controls where the
 * rest of commerce has one, and the only place in the concierge that still said
 * "cart".
 *
 * Clicking it closes the product detail panel first. The two are both
 * right-hand surfaces, so leaving the panel open would either cover the bag or
 * be covered by it depending on the layout; closing is the only reading that
 * works in both.
 *
 * Two variants, because the button has two homes:
 *  - inline (default) — sits in a header's right group, before Share: the
 *    desktop title bar, and the mobile nav below 768px. In-flow, so it can
 *    never cover Share or Close the way a floating variant did (both were
 *    anchored to the same `top-right` corner, each unaware of the other).
 *  - floating — an absolute corner button. Still needed wherever neither
 *    header renders: 768–1279px, and the welcome screen at 768px and up.
 */
interface ConciergeBagButtonProps {
  /** Absolute top-right button instead of an in-flow one. */
  floating?: boolean;
  /** Hide at >=1280px, where the title bar renders the inline button instead. */
  hideOnDesktop?: boolean;
  /** Button and icon size in px. The mobile nav passes its own icon size. */
  size?: number;
  /** Icon colour. The mobile nav passes its icon colour. Else the root's text colour. */
  color?: string;
}

const HIDE_ON_DESKTOP_CLASS = 'concierge-bag-button-mobile-only';
const ICON_SIZE = 30;
const BUTTON_SIZE = 42;

/**
 * The bag, with the count drawn inside it.
 *
 * The digits are SVG `<text>`, not an HTML span on top: the glyph then scales
 * and centres with the artwork instead of being positioned against it in pixels,
 * which is what made "1" sit off-centre at small sizes.
 */
function BagIcon({ size, count }: { size: number; count: number | null }) {
  const label = count == null ? null : count > 99 ? '99+' : String(count);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Handle, then the bag body — an outline tote, matching the storefront's. */}
      <path d="M11 12V9.5a5 5 0 0 1 10 0V12" />
      <rect x="5.5" y="12" width="21" height="16.5" rx="3.5" />
      {label && (
        <text
          x="16"
          y="21.6"
          textAnchor="middle"
          dominantBaseline="middle"
          stroke="none"
          fill="currentColor"
          fontSize={label.length > 2 ? 8.5 : 11}
          fontWeight={700}
          fontFamily="inherit"
        >
          {label}
        </text>
      )}
    </svg>
  );
}

const ConciergeBagButton = ({ floating, hideOnDesktop, size, color }: ConciergeBagButtonProps) => {
  const { commerce, closeProductPanel } = useConciergeContext();
  const { bag } = useBagWords();
  const [itemCount, setItemCount] = useState(() => commerce?.getCartState().itemCount ?? 0);

  useEffect(() => {
    if (!commerce) return;
    setItemCount(commerce.getCartState().itemCount);
    const unsubscribe = commerce.subscribe((s) => setItemCount(s.itemCount));
    // Pick up a bag the shopper filled outside the concierge too.
    commerce.refreshCart();
    return unsubscribe;
  }, [commerce]);

  // No adapter means this app has no bag on this surface — not an empty bag.
  if (!commerce) return null;

  const hasItems = itemCount > 0;

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: hideOnDesktop ? `@media(min-width:1280px){.${HIDE_ON_DESKTOP_CLASS}{display:none!important}}` : '',
        }}
      />
      <button
        type="button"
        className={hideOnDesktop ? HIDE_ON_DESKTOP_CLASS : undefined}
        aria-label={hasItems ? `View ${bag}, ${itemCount} item${itemCount === 1 ? '' : 's'}` : `View ${bag}`}
        onClick={() => {
          closeProductPanel();
          commerce.goToCart();
        }}
        // Opts out of the concierge stylesheet's grey `button[aria-label]`
        // override, which is meant for header icon buttons.
        data-concierge-filled-action=""
        style={{
          // The floating variant is positioned differently, but it is the SAME
          // control: no chrome of its own. It used to carry a white disc with a
          // border and a drop shadow, which read as a second, heavier button on
          // the welcome screen than the one in the title bar.
          ...(floating ? { top: 12, right: 16, zIndex: 10 } : {}),
          position: floating ? 'absolute' : 'relative',
          background: 'none',
          border: 'none',
          borderRadius: 8,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size ?? BUTTON_SIZE,
          height: size ?? BUTTON_SIZE,
          padding: 0,
          cursor: 'pointer',
          color: color ?? 'inherit',
          flexShrink: 0,
        }}
      >
        <BagIcon size={size ?? ICON_SIZE} count={hasItems ? itemCount : null} />
      </button>
    </>
  );
};

export default ConciergeBagButton;
